<template>
  <div
    v-if="open"
    class="fixed z-50 w-72 rounded-xl border border-[#e4ded4] bg-white p-2 shadow-xl"
    :style="{ top: `${top}px`, left: `${left}px` }"
    role="menu"
    aria-label="Liens courts"
    @mouseenter="emit('keep')"
    @mouseleave="emit('leave')"
  >
    <p v-if="loading" class="px-3 py-2 text-xs text-[#8a847a]">Chargement…</p>
    <p v-else-if="!links.length" class="px-3 py-2 text-xs text-[#8a847a]">Aucun lien actif.</p>
    <a
      v-for="link in links"
      :key="link.id"
      :href="link.short_url"
      target="_blank"
      rel="noopener noreferrer"
      role="menuitem"
      class="group flex items-center justify-between gap-3 rounded-lg px-3 py-2 transition hover:bg-[#f6f3ee]"
    >
      <span class="min-w-0">
        <span class="block truncate text-xs text-[#141414]">{{ link.label }}</span>
        <span class="block truncate text-[10px] tracking-[0.04em] text-[#8a847a]">/s/{{ link.code }} · {{ link.click_count }} clics</span>
      </span>
      <ExternalLink class="h-3.5 w-3.5 shrink-0 text-[#8a847a] group-hover:text-[#141414]" aria-hidden="true" />
    </a>
    <NuxtLink to="/app/liens" class="mt-1 block rounded-lg px-3 py-2 text-[10px] uppercase tracking-[0.14em] text-[#5c574f] transition hover:bg-[#f6f3ee] hover:text-[#141414]">
      Gérer les liens
    </NuxtLink>
  </div>
</template>

<script setup lang="ts">
import { ExternalLink } from 'lucide-vue-next'

interface FlyoutLink {
  id: number
  code: string
  label: string
  short_url: string
  active: boolean
  click_count: number
}

const props = defineProps<{ open: boolean, top: number, left: number }>()
const emit = defineEmits<{ keep: [], leave: [] }>()

const { authHeaders } = useAtelierSession()
const links = ref<FlyoutLink[]>([])
const loading = ref(false)
let loadedAt = 0

// Loaded on first hover, then reused for a minute: no polling.
watch(() => props.open, async (isOpen) => {
  if (!isOpen || loading.value || Date.now() - loadedAt < 60_000) return
  loading.value = true
  try {
    const response = await $fetch<{ links: FlyoutLink[] }>('/api/app2/links', { headers: await authHeaders() })
    links.value = (response.links || [])
      .filter(link => link.active)
      .sort((a, b) => b.click_count - a.click_count)
      .slice(0, 8)
    loadedAt = Date.now()
  } catch {
    links.value = []
  } finally {
    loading.value = false
  }
})
</script>
